import { CommonModule, DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { ExternalEoiService } from '@services/api/external-eoi.service';
import { ExternalEoiPdfService } from '@modules/my-projects/edit-project/project-edit/external-eoi/external-eoi-pdf.service';
import {
  EXTERNAL_EOI_CUSTOM_SECTION_FALLBACK_LABEL,
  EXTERNAL_EOI_DEFAULT_WORDING,
  EXTERNAL_EOI_PREVIEW_STORAGE_KEY,
  EXTERNAL_EOI_PRICE_KEYS,
  EXTERNAL_EOI_PROPERTY_META_KEYS,
  EXTERNAL_EOI_SECTIONS,
  ExternalEoiConfigField,
  ExternalEoiSectionRun,
  ExternalEoiWording,
  buildExternalEoiRetailPdfData,
  buildExternalEoiSectionRuns,
  buildExternalEoiSubmission,
  externalEoiMetaLabel,
  externalEoiPropertyColumnsClass,
  externalEoiSpecIcon,
  flattenExternalEoiAnswerableFields,
  isExternalEoiRetailPdfDataMostlyEmpty,
  resolveExternalEoiField,
  resolveExternalEoiFieldWidth,
  resolveExternalEoiInfoValue,
  resolveExternalEoiPropertyColumns,
  resolveExternalEoiWording,
} from '@modules/my-projects/edit-project/project-edit/external-eoi/external-eoi.model';

/** All page states the buyer form can be in. */
type EoiPageState = 'loading' | 'otp' | 'ready' | 'submitted' | 'expired' | 'cancelled' | 'not_found' | 'already_completed';

/** Where the OTP grant is kept so a refresh doesn't force a re-verify. */
const ACCESS_STORAGE_PREFIX = 'eoi_access_';

/**
 * ExternalEoiFormComponent
 *
 * Public, unauthenticated buyer-facing Expression of Interest form.
 * Route: /external-eoi/:token
 *
 * Loads the project's configured field list + prefilled values from the
 * secure token, renders a YPM-branded mobile-friendly form, and submits back
 * to the backend which creates/updates the EOI record.
 */
@Component({
  selector: 'app-external-eoi-form',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule],
  providers: [DatePipe],
  templateUrl: './external-eoi-form.component.html',
  styleUrls: ['./external-eoi-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ExternalEoiFormComponent implements OnInit {
  /**
   * Set by an agent-side host ("View Link" on a lead/EOI record or the project's link tables) to
   * the real link being viewed. Turns on the "Save for buyer" action: whatever the agent types is
   * saved against that link as `draft_answers`, so the buyer's own form opens already filled in —
   * the "I need a save button before I send the link to my client" request. Never set on the
   * builder's Preview (no link behind it) or on the public buyer route.
   */
  @Input() draftLinkId: string | null = null;
  /** Fired after a successful "Save for buyer", so the host can refresh its link list/status. */
  @Output() draftSaved = new EventEmitter<Record<string, any>>();

  private readonly route = inject(ActivatedRoute);
  private readonly fb = inject(FormBuilder);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly externalEoi = inject(ExternalEoiService);
  private readonly toaster = inject(ToastrService);
  private readonly eoiPdf = inject(ExternalEoiPdfService);

  token = '';

  /** Overall page state. */
  state: EoiPageState = 'loading';

  /**
   * Every fixed piece of copy on the page — heading, intro, the notice/privacy blocks, the submit
   * button, the thank-you screen — the config's overrides resolved over the defaults (see
   * `resolveExternalEoiWording`). One object rather than nine loose fields so a new block can't be
   * added to the template without going through the same fallback.
   */
  wording: ExternalEoiWording = { ...EXTERNAL_EOI_DEFAULT_WORDING };

  /** True while a "Save for buyer" PATCH is in flight. */
  savingDraft = false;

  /** The agent-side "Save for buyer" action — only on a real link viewed from inside the app. */
  get canSaveDraft(): boolean {
    return this.isPreview && !!this.draftLinkId;
  }

  /**
   * Branding resolved by the backend from the project's Sub Office
   * (`sub_office_id` → background_color / text_color / secondary_color / logo),
   * with `banner_image` taken from the first Project Slide Show image.
   * The values here are the fallback when a sub office sets nothing.
   */
  /**
   * Office logo used until the backend sends one on `branding.logo`.
   *
   * `normaliseBranding` lets any real value win, so an office that supplies its
   * own logo overrides this — see §1 of docs/backend/external-eoi-round3.md.
   */
  private static readonly FALLBACK_OFFICE_LOGO = 'https://remmi-xm.s3.ap-southeast-2.amazonaws.com/images/office_1762840054694.png';

  branding: any = {
    /** The PROJECT's name (header) — despite the field name, `company_name` is never the agency. */
    company_name: null,
    /** The agency's name (footer) — YPM Group until the backend sends `branding.office_name`. */
    office_name: 'YPM Group',
    /**
     * `logo` is resolved by the backend from the project's Branding tab first
     * and only then the sub office, so on a branded project it carries the
     * PROJECT's logo. It therefore feeds the project slot, and the office slot
     * reads `office_logo` — see the two getters below.
     */
    logo: null,
    /** The project's own logo, where the backend distinguishes it. */
    project_logo: null,
    /** The office/agency logo (YPM). */
    office_logo: null,
    banner_image: null,
    background_color: '#16603b',
    text_color: '#ffffff',
    secondary_color: '#16603b',
    /**
     * Listing agent's sign-off, shown in the footer. Not sent by the backend yet — see
     * docs/backend/external-eoi-round6.md §2. The footer simply doesn't render this block until
     * `agent_name` arrives.
     */
    agent_name: null,
    agent_email: null,
    agent_phone: null,
    /** Separate from `agent_phone` — "please include the mobile number too" on the footer's Agent Details. */
    agent_mobile: null,
  };

  /** Project name, needed on the passcode screen before the form is released. */
  projectName = '';

  /**
   * Office logo — shown on the LEFT of every brand strip.
   *
   * Falls back to YPM's asset until the backend sends `branding.office_logo`;
   * `branding.logo` is deliberately not used here because on a branded project
   * it holds the project's logo, which belongs in the other slot.
   */
  get officeLogo(): string | null {
    return this.branding?.office_logo || ExternalEoiFormComponent.FALLBACK_OFFICE_LOGO;
  }

  /** Project logo — shown on the RIGHT of every brand strip. */
  get projectLogo(): string | null {
    return this.branding?.project_logo || this.branding?.logo || null;
  }

  /**
   * The footer's agent phone, or `null` if the backend sent a malformed value.
   *
   * Seen live: `branding.agent_phone` arriving as the literal string `"+61undefined"` — a
   * server-side `` `+61${agent.mobile}` `` built on a mobile number that resolved to `undefined`
   * (see docs/backend/external-eoi-round6.md §2 update). Hiding rather than rendering it verbatim,
   * since a buyer seeing "+61undefined" reads as broken far worse than the agent's phone simply not
   * being shown.
   */
  get agentPhone(): string | null {
    return this.usableAgentNumber(this.branding?.agent_phone);
  }

  /**
   * The footer's agent mobile — "Agent Details: please include the mobile number too".
   *
   * Its own line rather than being folded into `agentPhone`: an agent can have both an office
   * number and a mobile, and a buyer ringing the wrong one is the problem this is meant to fix.
   * Several spellings are tried because the backend has not settled on one (see
   * external-eoi-client-review-2026-09-09-backend-requirements.md §4); suppressed when it merely
   * repeats `agentPhone`, so a backend sending the same number under both keys renders one line.
   */
  get agentMobile(): string | null {
    const mobile =
      this.usableAgentNumber(this.branding?.agent_mobile) ||
      this.usableAgentNumber(this.branding?.agent_mobile_number) ||
      this.usableAgentNumber(this.branding?.agent_phone_mobile);
    return mobile && mobile !== this.agentPhone ? mobile : null;
  }

  /**
   * One agent contact number, or `null` if the backend sent a malformed value.
   *
   * Seen live: `branding.agent_phone` arriving as the literal string `"+61undefined"` — a
   * server-side `` `+61${agent.mobile}` `` built on a mobile number that resolved to `undefined`
   * (see docs/backend/external-eoi-round6.md §2 update). Hiding rather than rendering it verbatim,
   * since a buyer seeing "+61undefined" reads as broken far worse than the agent's number simply
   * not being shown.
   */
  private usableAgentNumber(value: unknown): string | null {
    if (!value || typeof value !== 'string') return null;
    const trimmed = value.trim();
    if (!trimmed || /undefined|null/i.test(trimmed)) return null;
    return trimmed;
  }

  /** Property/lot summary shown in the details card. */
  property: any = null;

  /** Configured fields (ordered) + section metadata. */
  fields: ExternalEoiConfigField[] = [];
  /** Catalogue section labels, used as the fallback for a section with no `sectionLabels` entry. */
  readonly sections = EXTERNAL_EOI_SECTIONS;

  /**
   * The form body as runs of consecutive same-section fields, in the agent's order — exactly the
   * grouping the builder canvas draws (one heading wherever the section changes). Computed once per
   * form load in `renderReadyForm`, not a live getter, matching this component's
   * explicit-mutation/`OnPush` discipline elsewhere. See `buildExternalEoiSectionRuns` for why runs
   * rather than one block per section.
   */
  sectionRuns: ExternalEoiSectionRun[] = [];

  /** The catalogue's default label for a section key, or `EXTERNAL_EOI_CUSTOM_SECTION_FALLBACK_LABEL` for a custom one with none. */
  defaultSectionLabel(key: string): string {
    return this.sections.find(s => s.key === key)?.label || EXTERNAL_EOI_CUSTOM_SECTION_FALLBACK_LABEL;
  }

  /** False for a run with no real content — e.g. only a custom section's empty-state `divider` anchor. */
  runHasFields(run: ExternalEoiSectionRun): boolean {
    return run.fields.some(f => f.type !== 'divider');
  }

  /** Per-section heading overrides from the project's config — see `ExternalEoiConfig.section_labels`. */
  sectionLabels: Record<string, string> = {};

  /** Property Details card column count from the project's config — see `ExternalEoiConfig.property_columns`. */
  propertyColumns = resolveExternalEoiPropertyColumns(null);

  /**
   * True only for the builder's "Preview", whose property values are invented (see
   * `ExternalEoiComponent.PREVIEW_PROPERTY`): a fixed 3-bed/2-bath/$750,000 apartment, never the
   * lot a link is actually for. The card is therefore always complete here and only as complete as
   * the lot's own record on a real link, which reads as "the generated link is missing everything"
   * when the two are compared side by side. Labelled rather than left to be discovered.
   */
  sampleProperty = false;

  /** Modifier class that both sizes the property grids and places the rule between their columns. */
  get propertyColumnsClass(): string {
    return externalEoiPropertyColumnsClass(this.propertyColumns);
  }

  /** The heading actually shown for a section: the agent's override, or the catalogue default. */
  sectionLabel(key: string, fallback: string): string {
    return (this.sectionLabels[key] || '').trim() || fallback;
  }

  /** CSS class for a field's configured row width — see `.eoi-grid`/`.eoi-field` in the stylesheet. */
  fieldWidthClass(f: ExternalEoiConfigField): string {
    return `w-${resolveExternalEoiFieldWidth(f)}`;
  }

  form: FormGroup = this.fb.group({});
  /** Uploaded file names keyed by field key (base64 stored in the form control). */
  fileNames: Record<string, string> = {};
  submitting = false;

  // ---- One-time passcode ---------------------------------------------------

  /** Access grant from a verified passcode; kept so a refresh doesn't re-prompt. */
  accessToken: string | null = null;
  /** Where the passcode was sent, masked by the backend (e.g. j•••@email.com). */
  otpSentTo = '';
  otpCode = '';
  otpSending = false;
  otpVerifying = false;
  otpError = '';
  /** True once a code has been sent, so the UI shows the code entry step. */
  otpSent = false;

  /**
   * True when a passcode can never be delivered — the link carries no email
   * address. Retrying is pointless, so the button is replaced by an
   * explanation of what to do instead.
   */
  otpUnavailable = false;

  /**
   * True when the OTP screen was raised to recover a submit whose access grant had gone stale, not
   * on first load. A buyer who has just spent real time filling in every field cannot be sent
   * through `loadForm()`'s fresh prefill on re-verifying — that rebuilds `form` from the backend's
   * prefill and discards everything they typed. Checked in `verifyOtp()`'s success handler: when
   * true, it returns straight to `ready` and resubmits the form already in memory instead.
   */
  private resubmitAfterOtp = false;

  /** Set by the backend when the buyer has not paid the $2,000 deposit. */
  depositReceived: boolean | null = null;

  /** The EOI record this link belongs to, so answers merge into it on submit. */
  eoiId: string | null = null;

  /**
   * Puts a form payload on screen in its `ready` state.
   *
   * Shared by the live buyer load and by the agent-facing preview. The project's External EOI tab
   * used to preview a hand-built copy of this form, so the two drifted and the preview stopped
   * resembling what the buyer got. One method, one template, one stylesheet — they cannot diverge.
   */
  private renderReadyForm(data: any): void {
    this.wording = resolveExternalEoiWording(data);
    this.property = data.property || null;
    this.depositReceived = data.deposit_received ?? null;
    this.sectionLabels = data.section_labels || {};
    this.propertyColumns = resolveExternalEoiPropertyColumns(data);
    this.sampleProperty = !!data.is_sample_property;
    this.fields = [...(data.fields || [])].sort((a: any, b: any) => (a.order ?? 0) - (b.order ?? 0));
    this.sectionRuns = buildExternalEoiSectionRuns(this.fields);
    // Answers the agent saved against the link ("Save for buyer") win over the CRM prefill for the
    // same key — the agent typed them deliberately, on top of whatever the record already held.
    // Merged here as well as (eventually) server-side, so it works the moment the payload carries
    // `draft_answers` — see external-eoi-client-review-2026-09-07-backend-requirements.md §1.
    this.buildForm({ ...(data.prefill || {}), ...(data.draft_answers || {}) });
    this.state = 'ready';
    this.cdr.markForCheck();
  }

  /**
   * Renders a supplied payload instead of fetching one — used both by the "View link" overlay
   * (bound as a template `@Input`) and by the new-tab "Preview" route (set once from `ngOnInit`
   * after reading the config back out of sessionStorage).
   *
   * A setter rather than a plain `@Input` so edits in the field builder repaint an open "View
   * link" preview.
   */
  @Input() set previewConfig(value: any | null) {
    this.isPreview = !!value;
    if (!value) return;
    if (value.branding) this.branding = this.normaliseBranding(value.branding);
    this.renderReadyForm(value);
  }

  /** True when driven by `previewConfig`, which suppresses the token load and disables submit. */
  isPreview = false;

  /**
   * Reads the config the builder's "Preview" button wrote to storage just before `window.open`-ing
   * this route in a new tab.
   *
   * `localStorage`, not `sessionStorage`, and never cleared after reading: this tab's `window.open`
   * lineage is exactly what a refresh, a manually-typed/pasted URL, or reopening a closed preview
   * tab all break — `sessionStorage` only survives on the one tab `window.open` created it in, so
   * any of those landed here with nothing to read and fell through to the same "not found" state a
   * dead buyer link shows. `localStorage` is shared by the whole browser regardless of how this tab
   * got here, and keeping the last preview around means this route always has something to show
   * instead of occasionally rendering as a broken link.
   */
  private readPreviewFromStorage(): any | null {
    try {
      const raw = localStorage.getItem(EXTERNAL_EOI_PREVIEW_STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  ngOnInit(): void {
    // Preview supplies its own payload and has no token or route of its own.
    if (this.isPreview) return;

    // Routed here as `/external-eoi/preview` from the builder's "Preview" button (opened in a new
    // tab so the agent can see the full-size buyer form). There is no token — the config an agent
    // hasn't saved yet can't have one — so it's read back from the sessionStorage the opener wrote
    // to just before `window.open`, which a same-page `window.open` inherits a copy of.
    if (this.route.snapshot.data['preview']) {
      const config = this.readPreviewFromStorage();
      if (config) this.previewConfig = config;
      else this.state = 'not_found';
      return;
    }

    const params = this.route.snapshot.paramMap;
    this.token = params.get('token') || '';
    if (this.token) {
      this.accessToken = this.readStoredAccess();
      this.loadForm();
      return;
    }

    // Readable URL: /{project}/eoi/{lot}/{client}. The slugs are resolved to
    // the link's token, after which the flow is identical to the token URL.
    const projectSlug = params.get('projectSlug');
    const lotSlug = params.get('lotSlug');
    const clientSlug = params.get('clientSlug');
    if (projectSlug && lotSlug && clientSlug) {
      this.resolveSlugToken(projectSlug, lotSlug, clientSlug);
      return;
    }

    this.state = 'not_found';
  }

  private resolveSlugToken(projectSlug: string, lotSlug: string, clientSlug: string): void {
    this.externalEoi.resolvePublicSlug(projectSlug, lotSlug, clientSlug).subscribe({
      next: (res: any) => {
        const token = res?.data?.token || res?.data?.link?.token;
        if (!token) return this.setState('not_found');
        this.token = token;
        this.accessToken = this.readStoredAccess();
        this.loadForm();
      },
      error: (err: any) => this.setState(this.terminalStateFromError(err) || 'not_found'),
    });
  }

  private storageKey(): string {
    return `${ACCESS_STORAGE_PREFIX}${this.token}`;
  }

  private readStoredAccess(): string | null {
    try {
      return sessionStorage.getItem(this.storageKey());
    } catch {
      return null;
    }
  }

  private storeAccess(value: string | null): void {
    try {
      if (value) sessionStorage.setItem(this.storageKey(), value);
      else sessionStorage.removeItem(this.storageKey());
    } catch {
      /* private browsing — fall back to in-memory only */
    }
  }

  /** Email the buyer a passcode. */
  sendOtp(): void {
    this.otpSending = true;
    this.otpError = '';
    this.otpUnavailable = false;
    this.externalEoi.requestPublicOtp(this.token).subscribe({
      next: (res: any) => {
        this.otpSending = false;
        this.otpSent = true;
        this.otpSentTo = res?.data?.sent_to || this.otpSentTo;
        this.cdr.markForCheck();
      },
      error: (err: any) => {
        this.otpSending = false;
        // A link deactivated while the buyer sat on this screen must say so
        // rather than invite another attempt that can never succeed. Sending a
        // passcode takes no user input, so there is nothing here for a 403/404
        // to mean except that the link itself is refused.
        const terminal = this.terminalStateFromError(err);
        if (terminal) return this.setState(terminal);

        const { httpStatus } = this.normaliseError(err);
        if (httpStatus === 403 || httpStatus === 404) return this.setState('cancelled');
        if (httpStatus === 410) return this.setState('expired');

        const message = this.readErrorMessage(err);

        // No email on the link means no passcode can ever arrive. Saying
        // "please try again" here sends the buyer round a loop; naming the
        // problem lets them go to their agent instead.
        if (this.isMissingEmailError(message)) {
          this.otpUnavailable = true;
          this.otpError =
            message ||
            "We don't have an email address on file for this link, so we can't send you a passcode. Please contact your agent to have the link reissued with your email address.";
          this.cdr.markForCheck();
          return;
        }

        // The backend's own wording is usually more specific than ours.
        this.otpError = message || 'We could not send the passcode. Please try again.';
        this.cdr.markForCheck();
      },
    });
  }

  /**
   * Normalise the two different error shapes this page receives.
   *
   * `ApiService.get()` re-throws the original `HttpErrorResponse`, so `status`
   * is the HTTP code and `error` holds the body. `ApiService.post()` runs
   * through `handleError`, which throws the BODY itself — there `status` is the
   * API's own boolean success flag rather than an HTTP code, and the message
   * sits at the top level. Reading only the HttpErrorResponse shape is why the
   * backend's "No email address is on file" never reached the buyer.
   */
  private normaliseError(err: any): { httpStatus: number | null; message: string; body: any } {
    if (typeof err === 'string') return { httpStatus: null, message: err, body: {} };

    // Only a numeric `status` is an HTTP code; the unwrapped body carries a boolean.
    const isHttpResponse = typeof err?.status === 'number';
    const rawBody = (isHttpResponse ? err?.error : err) ?? {};
    const body = typeof rawBody === 'string' ? (this.tryParseJson(rawBody) ?? rawBody) : rawBody;

    const message = [body?.message, body?.data?.message, typeof body === 'string' ? body : null, !isHttpResponse ? err?.message : null].find(
      (m: any) => typeof m === 'string' && m.trim()
    );

    return {
      httpStatus: isHttpResponse ? err.status : null,
      message: (message || '').trim(),
      body: typeof body === 'object' && body ? body : {},
    };
  }

  private tryParseJson(text: string): any {
    try {
      return JSON.parse(text);
    } catch {
      return null;
    }
  }

  /**
   * A human-readable message from an error response, when there is one.
   *
   * Only plain prose is used — anything carrying markup, a URL or what looks
   * like a stack frame is dropped in favour of our own wording, so internal
   * detail is never shown to a buyer.
   */
  private readErrorMessage(err: any): string {
    const text = this.normaliseError(err).message;
    if (!text || text.length > 220) return '';
    if (/[<>{}]|https?:\/\/|\bat\s+\w+\.\w+/i.test(text)) return '';
    return text;
  }

  /** The link has no email address, so a passcode cannot be delivered. */
  private isMissingEmailError(message: string): boolean {
    const text = (message || '').toLowerCase();
    if (!text.includes('email')) return false;
    return /\bno\b|not on file|on file|missing|not found|not available|unavailable/.test(text);
  }

  /**
   * The access grant has gone stale and the backend is asking for the passcode again — seen live
   * as a "Please verify your passcode to continue" body message on a submit that did NOT come back
   * as a 401/403 (some other status this backend uses for the same condition). Caught by wording
   * rather than status code for the same reason `isMissingEmailError` is: the status alone doesn't
   * reliably say what actually went wrong.
   */
  private isAuthRequiredError(message: string): boolean {
    const text = (message || '').toLowerCase();
    return text.includes('passcode') && (text.includes('verify') || text.includes('continue'));
  }

  verifyOtp(): void {
    const code = (this.otpCode || '').trim();
    if (!code) {
      this.otpError = 'Enter the passcode from your email.';
      return;
    }
    this.otpVerifying = true;
    this.otpError = '';
    this.externalEoi.verifyPublicOtp(this.token, code).subscribe({
      next: (res: any) => {
        this.otpVerifying = false;
        const grant = res?.data?.access || null;
        if (!grant) {
          this.otpError = 'That passcode did not work. Please try again.';
          this.cdr.markForCheck();
          return;
        }
        this.accessToken = grant;
        this.storeAccess(grant);
        this.otpCode = '';

        // Recovering a stale-grant submit: the buyer's answers are still sitting in `form`, so
        // resubmit them directly rather than routing through `loadForm()`, which would rebuild the
        // form from the backend's prefill and lose everything just typed.
        if (this.resubmitAfterOtp) {
          this.resubmitAfterOtp = false;
          this.state = 'ready';
          this.cdr.markForCheck();
          this.submit();
          return;
        }

        this.state = 'loading';
        this.cdr.markForCheck();
        this.loadForm();
      },
      error: (err: any) => {
        this.otpVerifying = false;
        const terminal = this.terminalStateFromError(err);
        if (terminal) return this.setState(terminal);
        const { httpStatus, message } = this.normaliseError(err);
        this.otpError =
          httpStatus === 429
            ? 'Too many attempts. Please wait and try again.'
            : this.readErrorMessage(err) || message || 'That passcode is incorrect or has expired.';
        this.cdr.markForCheck();
      },
    });
  }

  loadForm(): void {
    this.externalEoi.getPublicForm(this.token, this.accessToken).subscribe({
      next: (res: any) => {
        const data = res?.data;
        if (!data) {
          this.state = 'not_found';
          this.cdr.markForCheck();
          return;
        }
        // Branding is applied before any branch returns so every state — the
        // passcode gate and the terminal screens included — is painted in the
        // project's colours and carries its logos.
        this.branding = this.normaliseBranding(data.branding);
        this.projectName = data.property?.project_name || data.project_name || this.projectName;

        // A dead link is checked BEFORE the passcode gate: asking someone to
        // verify their way into a link that has been deactivated sends them
        // round a loop that cannot succeed.
        const terminal = this.terminalStateFromPayload(data);
        if (terminal) return this.setState(terminal);

        // The form holds personal details, so the backend can demand a passcode
        // before releasing them. Nothing sensitive is in this response.
        if (data.otp_required) {
          this.otpSentTo = data.otp_sent_to || '';
          this.accessToken = null;
          this.storeAccess(null);
          return this.setState('otp');
        }

        if (data.status === 'submitted' && data.lock_after_submit) return this.setState('already_completed');

        // The EOI this link was generated against, echoed back on submit so the
        // answers merge into that exact record rather than creating a new one.
        this.eoiId = data.eoi_id || data.eoi?._id || null;
        this.renderReadyForm(data);
      },
      error: (err: any) => {
        const { httpStatus, body } = this.normaliseError(err);

        // A dead link commonly comes back as an HTTP error rather than a 200, and until now
        // this branch never looked at the body for it — so a deactivated/expired link fell back
        // to the component's construction-time defaults (no project logo, "YPM Group") instead
        // of the actual project, even though the success path above does exactly this. Mirrors
        // that same extraction so every state is painted consistently.
        const errData = body?.data || body || {};
        if (errData.branding) this.branding = this.normaliseBranding(errData.branding);
        this.projectName = errData.property?.project_name || errData.project_name || this.projectName;

        // A deactivated or expired link is a dead end, not an auth problem.
        const terminal = this.terminalStateFromError(err);
        if (terminal) return this.setState(terminal);

        const hadGrant = !!this.accessToken;
        this.accessToken = null;
        this.storeAccess(null);

        // A 403 while holding no grant is the link itself being refused: the
        // passcode gate is signalled by a 200 carrying `otp_required`, never by
        // a 403. Only a 403 against a grant we were actually presenting means
        // that grant went stale and re-verifying is worth offering. Without
        // this split, a deactivated link kept showing "Verify it's you" and no
        // passcode could ever open it.
        if (httpStatus === 403 && !hadGrant) return this.setState('cancelled');
        if (httpStatus === 401 || httpStatus === 403) return this.setState('otp');

        this.state = httpStatus === 410 ? 'expired' : 'not_found';
        this.cdr.markForCheck();
      },
    });
  }

  /**
   * Read a terminal link state out of a successful payload.
   *
   * Deliberately generous about shape: the status has been seen as `status`,
   * `link_status`, `state`, and as booleans (`cancelled`, `is_active: false`).
   * Missing it means the buyer gets sent to the passcode screen for a link
   * that can never open, so every known spelling is accepted.
   */
  private terminalStateFromPayload(data: any): EoiPageState | null {
    if (!data) return null;

    if (data.cancelled === true || data.deactivated === true || data.is_cancelled === true || data.is_deactivated === true) return 'cancelled';
    if (data.expired === true || data.is_expired === true) return 'expired';
    // `is_active`/`active` are only meaningful when explicitly false.
    if (data.is_active === false || data.active === false) return 'cancelled';

    const raw = String(data.status ?? data.link_status ?? data.state ?? '').toLowerCase();
    if (raw.includes('cancel') || raw.includes('deactivat') || raw.includes('revok')) return 'cancelled';
    if (raw.includes('expire')) return 'expired';
    return null;
  }

  /**
   * Read a terminal link state out of an error response.
   *
   * The status may arrive as `data.status`, a bare `status` field or a coded
   * message depending on the endpoint, so all three are accepted rather than
   * relying on one shape.
   */
  private terminalStateFromError(err: any): EoiPageState | null {
    const { message, body } = this.normaliseError(err);
    // `body.status` is a boolean on the unwrapped-POST shape, which stringifies
    // harmlessly to "false" and matches nothing.
    const raw = [body?.data?.status, body?.status, body?.code, message]
      .map(v => String(v ?? ''))
      .join(' ')
      .toLowerCase();
    if (raw.includes('cancel') || raw.includes('deactivat') || raw.includes('revok')) return 'cancelled';
    if (raw.includes('expire')) return 'expired';
    return null;
  }

  /**
   * Shown to the buyer when the deposit has not been received: submitting the
   * EOI does not take the property off the market.
   */
  get showsNoReservationWarning(): boolean {
    return this.depositReceived === false;
  }

  private setState(s: EoiPageState): void {
    this.state = s;
    this.cdr.markForCheck();
  }

  /**
   * Merge backend branding over the defaults, ignoring blank values so an
   * unset colour falls back rather than painting the page empty. The backend
   * resolves colours from the project's Branding tab first, then its Sub
   * Office. Project branding names its fill `primary_color` while a sub office
   * names it `background_color`, so `primary_color` is accepted as an alias.
   */
  private normaliseBranding(incoming: any): any {
    const merged = { ...this.branding };
    const src = incoming || {};
    if (src.primary_color && !src.background_color) src.background_color = src.primary_color;
    for (const [key, value] of Object.entries(src)) {
      if (value !== null && value !== undefined && value !== '') merged[key] = value;
    }
    return merged;
  }

  /**
   * States that end the journey. They share one card and one message so the page never tells an
   * unauthenticated visitor which kind of dead link they hold.
   */
  get isTerminalState(): boolean {
    return this.state === 'not_found' || this.state === 'expired' || this.state === 'cancelled' || this.state === 'already_completed';
  }

  /** Header/footer/button fill. */
  get brandBg(): string {
    return this.branding?.background_color || '#16603b';
  }

  /** Text drawn on top of `brandBg`. */
  get brandText(): string {
    return this.branding?.text_color || '#ffffff';
  }

  /** Accent used for focus rings, links and the upload hover state. */
  get brandAccent(): string {
    return this.branding?.secondary_color || this.brandBg;
  }

  /**
   * Header background: the slide-show photo under a left-to-right scrim so the
   * logo stays legible, falling back to the flat brand colour with no photo.
   */
  get bannerStyle(): string | null {
    const img = this.branding?.banner_image;
    if (!img) return null;
    return `linear-gradient(90deg, ${this.hexToRgba(this.brandBg, 0.92)} 0%, ${this.hexToRgba(this.brandBg, 0.55)} 45%, ${this.hexToRgba(
      this.brandBg,
      0.1
    )} 100%), url("${encodeURI(img)}")`;
  }

  /** #rgb / #rrggbb → rgba(). Falls back to the brand green on anything else. */
  private hexToRgba(hex: string, alpha: number): string {
    let h = (hex || '').trim().replace('#', '');
    if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
    if (!/^[0-9a-fA-F]{6}$/.test(h)) return `rgba(22, 96, 59, ${alpha})`;
    const n = parseInt(h, 16);
    return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
  }

  /**
   * The prefilled value for one field.
   *
   * The backend's `prefill` map is keyed by field on the live record it read the data from, which
   * is not always this field's builder `key` — `full_name` (builder key) resolves via
   * `resolveExternalEoiField` (the same key → EOI-column mapping `buildExternalEoiSubmission`
   * already uses for the reverse direction, on submit) to `client_first_name`, the underlying
   * EOI/contact record's own field name. A prefill map keyed by that resolved name left
   * `prefill[f.key]` empty even though the value was sent under a different key in the same
   * payload. Falling back to the resolved EOI field recovers that case without guessing at every
   * possible alias the backend might use.
   */
  private prefillValueFor(f: ExternalEoiConfigField, prefill: Record<string, any>): any {
    if (prefill[f.key] != null) return prefill[f.key];
    const eoiField = resolveExternalEoiField(f);
    if (eoiField && prefill[eoiField] != null) return prefill[eoiField];
    return '';
  }

  /** Build the reactive form from the field config + prefilled values. */
  private buildForm(prefill: Record<string, any>): void {
    const group: Record<string, any> = {};
    for (const f of flattenExternalEoiAnswerableFields(this.fields)) {
      if (f.type === 'info' || f.type === 'static_text' || f.type === 'divider') continue; // display-only, not part of the form
      const validators = f.required ? [Validators.required] : [];
      if (f.type === 'email') validators.push(Validators.email);
      // checkbox holds an array of the ticked options — Angular's `Validators.required` already
      // treats an empty array as unmet, so `required` needs no special handling here.
      const prefilled = this.prefillValueFor(f, prefill);
      const defaultValue = f.type === 'checkbox' ? [] : '';
      const value = { value: prefilled ?? defaultValue, disabled: f.readOnly || (prefilled != null && prefilled !== '' && !f.allowEditing) };
      group[f.key] = [value, validators];
    }
    this.form = this.fb.group(group);
  }

  /** True when `opt` is one of the currently-ticked options on a `checkbox` field. */
  isChecked(f: ExternalEoiConfigField, opt: string): boolean {
    const value = this.form.get(f.key)?.value;
    return Array.isArray(value) && value.includes(opt);
  }

  /** Adds/removes `opt` from a `checkbox` field's ticked-options array. */
  toggleCheckboxOption(f: ExternalEoiConfigField, opt: string, checked: boolean): void {
    const control = this.form.get(f.key);
    if (!control) return;
    const current: string[] = Array.isArray(control.value) ? control.value : [];
    const next = checked ? [...current, opt] : current.filter(v => v !== opt);
    control.setValue(next);
    control.markAsDirty();
  }

  // Field order within a section is the agent's own (`sectionRuns`), with no special-casing: this
  // used to pin Full Name / Purchaser 1 to the top of "Your Details" regardless of the saved order,
  // which was one more way the buyer form and the builder canvas disagreed. The canvas is the
  // source of truth now — drag the name fields up there and they lead here too (the default
  // template already puts them first).

  /**
   * Info fields display the prefilled property value.
   *
   * Delegates to `resolveExternalEoiInfoValue`, shared with the PDF export
   * (`buildExternalEoiPdfPayload`) so a real link's Property Details reads the same on both — see
   * that function for the alias table this tries every candidate spelling against.
   */
  infoValue(f: ExternalEoiConfigField): string {
    return resolveExternalEoiInfoValue(f, this.property);
  }

  /** Property fields the agent put on the form, in order. */
  private get propertyFields(): ExternalEoiConfigField[] {
    return this.fields.filter(f => f.section === 'property');
  }

  /**
   * A selected property field is listed whether or not the payload carries a value for it — an
   * empty one renders as "—", the same convention the body's own `info` fields already use.
   *
   * These used to be filtered by `!!infoValue(f)`, so a field the agent had deliberately put on the
   * form simply vanished from a real link when its lot had nothing recorded. Reported repeatedly as
   * "Property Details info are missing but they are added": the fields WERE added, and silently
   * dropping them made an empty lot record indistinguishable from a broken form. Showing the row
   * with a dash says which of the two it is — and puts the gap where someone can see and fill it.
   */
  get propertyMetaFields(): ExternalEoiConfigField[] {
    return this.propertyFields.filter(f => EXTERNAL_EOI_PROPERTY_META_KEYS.includes(f.key));
  }

  /**
   * Everything else in the property section becomes an icon spec tile — bed,
   * bath, car, type, level, areas, price. Deriving it this way (rather than a
   * fixed key list) means any property field the agent adds shows up.
   */
  get propertySpecFields(): ExternalEoiConfigField[] {
    return this.propertyFields.filter(f => !EXTERNAL_EOI_PROPERTY_META_KEYS.includes(f.key));
  }

  /**
   * Every spec row in one list — the dollar figures first (Price, Total Price, Car Park/Storage
   * Price: the numbers a buyer scans for), then bed/bath/level/aspect/areas/storage.
   *
   * One list, and therefore ONE grid. These were two separate `dl`s, and a grid restarts at column
   * one: with the card in three columns the price block's last row left empty cells sitting in the
   * middle of the card before the next block began — "don't leave this columns". A single grid
   * flows straight through, so the only ragged row is the last one on the card.
   */
  get propertySpecRows(): ExternalEoiConfigField[] {
    const specs = this.propertySpecFields;
    return [...specs.filter(f => this.isPriceSpec(f)), ...specs.filter(f => !this.isPriceSpec(f))];
  }

  /** Only draw the details card when the agent selected something to show. */
  get showPropertyCard(): boolean {
    return !!this.property && (this.propertyMetaFields.length > 0 || this.propertySpecFields.length > 0);
  }

  metaLabel(f: ExternalEoiConfigField): string {
    return externalEoiMetaLabel(f);
  }

  /** Icon for a spec tile, taken from the field catalogue so new property fields get one for free. */
  specIcon(f: ExternalEoiConfigField): string {
    return externalEoiSpecIcon(f);
  }

  isPriceSpec(f: ExternalEoiConfigField): boolean {
    return EXTERNAL_EOI_PRICE_KEYS.includes(f.key);
  }

  /**
   * Footer copyright year — always today's year. `property.year` (the project's
   * completion date) was shown here previously, which put a future construction
   * date in a copyright notice.
   */
  get copyrightYear(): number {
    return new Date().getFullYear();
  }

  isInvalid(f: ExternalEoiConfigField): boolean {
    const c = this.form.get(f.key);
    return !!c && c.invalid && (c.touched || c.dirty);
  }

  async onFileChange(event: Event, f: ExternalEoiConfigField): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    // The native `accept` attribute is only a picker hint — most OS file dialogs let a buyer
    // choose "All Files" and pick anything, so the extension still has to be checked here.
    if (!this.isFileTypeAllowed(file, f)) {
      this.toaster.error(`"${file.name}" is not an accepted file type. Please upload one of: ${f.fileConfig?.accept || 'the listed types'}.`);
      input.value = '';
      return;
    }

    const maxBytes = (f.fileConfig?.maxSizeMb ?? 10) * 1024 * 1024;
    if (file.size > maxBytes) {
      this.toaster.error(`File must be under ${f.fileConfig?.maxSizeMb ?? 10}MB.`);
      input.value = '';
      return;
    }

    this.preparingFile[f.key] = true;
    this.cdr.markForCheck();
    try {
      const base64 = await this.encodeUpload(file);
      this.form.get(f.key)?.setValue({ name: file.name, data: base64 });
      this.form.get(f.key)?.markAsDirty();
      this.fileNames[f.key] = file.name;
    } finally {
      this.preparingFile[f.key] = false;
      this.cdr.markForCheck();
    }
  }

  /** Checks `file`'s extension against the field's configured `accept` list (e.g. `.pdf,.jpg,.jpeg,.png`). */
  private isFileTypeAllowed(file: File, f: ExternalEoiConfigField): boolean {
    const accept = f.fileConfig?.accept?.trim();
    if (!accept) return true; // no restriction configured
    const ext = `.${file.name.split('.').pop()?.toLowerCase() || ''}`;
    const allowed = accept
      .split(',')
      .map(a => a.trim().toLowerCase())
      .filter(Boolean);
    return allowed.includes(ext);
  }

  /** Clears a chosen file so the buyer can pick a different one without re-opening the field. */
  removeFile(event: Event, f: ExternalEoiConfigField, input: HTMLInputElement): void {
    event.preventDefault();
    event.stopPropagation();
    input.value = '';
    delete this.fileNames[f.key];
    this.form.get(f.key)?.setValue(null);
    this.form.get(f.key)?.markAsPristine();
    this.cdr.markForCheck();
  }

  /** Fields currently being read/compressed, so the dropzone can say so. */
  preparingFile: Record<string, boolean> = {};

  /** Longest edge, in px, an uploaded photo is scaled down to. */
  private static readonly IMAGE_MAX_EDGE = 1800;
  /** JPEG quality used when re-encoding a downscaled photo. */
  private static readonly IMAGE_QUALITY = 0.8;

  /**
   * Read a file to a base64 data URL, downscaling photos on the way.
   *
   * Answers are posted inline as base64, which inflates the payload by ~33%.
   * A phone photo of a passport is commonly 4–8MB, so two uploads could push a
   * single submit past 20MB — that is what made submitting take over half a
   * minute. Scaling to a long edge of 1800px (still comfortably legible for an
   * ID or a deposit slip) typically cuts that by more than 10x.
   *
   * PDFs and anything non-image pass straight through untouched, as do images
   * that are already small. Any failure falls back to the original file, so a
   * browser without canvas support still uploads.
   */
  private async encodeUpload(file: File): Promise<string> {
    const original = await this.toBase64(file);
    if (!file.type?.startsWith('image/') || file.type === 'image/gif') return original;
    // Under ~600KB the round trip costs more than it saves.
    if (file.size < 600 * 1024) return original;

    try {
      const compressed = await this.downscaleImage(original);
      // Keep whichever is actually smaller — re-encoding can inflate an
      // already well-compressed image.
      return compressed && compressed.length < original.length ? compressed : original;
    } catch {
      return original;
    }
  }

  private downscaleImage(dataUrl: string): Promise<string | null> {
    return new Promise(resolve => {
      const img = new Image();
      img.onload = () => {
        const { width, height } = img;
        const longest = Math.max(width, height);
        const scale = longest > ExternalEoiFormComponent.IMAGE_MAX_EDGE ? ExternalEoiFormComponent.IMAGE_MAX_EDGE / longest : 1;
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(width * scale);
        canvas.height = Math.round(height * scale);
        const ctx = canvas.getContext('2d');
        if (!ctx) return resolve(null);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL('image/jpeg', ExternalEoiFormComponent.IMAGE_QUALITY));
      };
      img.onerror = () => resolve(null);
      img.src = dataUrl;
    });
  }

  private toBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.toaster.error('Please complete the required fields.');
      this.cdr.markForCheck();
      return;
    }
    // Blanks are stripped and the key → EOI-column mapping is sent alongside
    // the answers, so an existing EOI is merged rather than blanked and the
    // backend never has to re-derive the mapping itself.
    const submission = buildExternalEoiSubmission(this.fields, this.form.getRawValue());
    if (!Object.keys(submission.values).length) {
      this.toaster.error('Please complete at least one field before submitting.');
      this.cdr.markForCheck();
      return;
    }

    this.submitting = true;
    const payload = {
      token: this.token,
      ...submission,
      // Names the EOI to merge into. The link already carries it server-side;
      // sending it back makes the target explicit rather than re-derived.
      eoi_id: this.eoiId,
      // Submitting an EOI must NOT take the lot off the market on its own —
      // only a confirmed $2,000 deposit does that. The flag travels with the
      // submission so the backend never has to infer the reservation rule
      // from the EOI's presence alone.
      deposit_received: this.depositReceived === true,
      hold_lot: this.depositReceived === true,
    };
    this.externalEoi.submitPublicForm(this.token, payload, this.accessToken).subscribe({
      next: () => {
        this.submitting = false;
        this.state = 'submitted';
        this.cdr.markForCheck();
      },
      error: (err: any) => {
        this.submitting = false;

        const terminal = this.terminalStateFromError(err);
        if (terminal) {
          this.toaster.error(terminal === 'expired' ? 'This link has expired.' : 'This link has been deactivated.');
          return this.setState(terminal);
        }

        // Grant expired mid-form — re-verify rather than losing the submission silently. Caught
        // either by status code or, since this backend doesn't always use 401/403 for it, by the
        // message itself (see `isAuthRequiredError`).
        const { httpStatus, message } = this.normaliseError(err);
        if (httpStatus === 401 || httpStatus === 403 || this.isAuthRequiredError(message)) {
          this.accessToken = null;
          this.storeAccess(null);
          this.resubmitAfterOtp = true;
          this.toaster.error('Your session expired. Please re-enter the passcode — your answers are still here.');
          return this.setState('otp');
        }
        this.toaster.error(this.readErrorMessage(err) || 'We could not submit your form. Please try again.');
        this.cdr.markForCheck();
      },
    });
  }

  /**
   * Agent-side "Save for buyer" — saves whatever is currently typed into the form against the link
   * as `draft_answers`, so the buyer's own form opens already filled in. Deliberately not a submit:
   * no validation (a half-filled form is the whole point), no EOI merge, no notification — only the
   * buyer's real submit does those. Blanks are dropped by the same rule as a submission, so the map
   * only ever carries what the agent actually entered.
   */
  saveDraft(): void {
    if (!this.draftLinkId || this.savingDraft) return;
    const draftAnswers = buildExternalEoiSubmission(this.fields, this.form.getRawValue()).values;
    this.savingDraft = true;
    this.cdr.markForCheck();
    this.externalEoi.saveLinkDraftAnswers(this.draftLinkId, draftAnswers).subscribe({
      next: () => {
        this.savingDraft = false;
        this.toaster.success("Saved — the buyer's form will open with these details filled in.");
        this.draftSaved.emit(draftAnswers);
        this.cdr.markForCheck();
      },
      error: (err: any) => {
        this.savingDraft = false;
        this.toaster.error(this.readErrorMessage(err) || 'Could not save these details. Please try again.');
        this.cdr.markForCheck();
      },
    });
  }

  /**
   * Agent-side "Download PDF" — next to "Save for buyer" on the same View Link overlay. Reads
   * straight off the live form (`getRawValue()` picks up disabled/prefilled controls too), so the
   * PDF always matches whatever is currently on screen — CRM prefill, a saved draft, or whatever the
   * agent has typed but not yet saved — without needing its own round trip.
   */
  downloadPdf(): void {
    const data = buildExternalEoiRetailPdfData({
      fields: this.fields,
      property: this.property,
      projectName: this.projectName,
      values: this.form.getRawValue(),
    });
    this.eoiPdf.download(data);
    if (isExternalEoiRetailPdfDataMostlyEmpty(data)) {
      this.toaster.warning(
        'This project’s External EOI form has no buyer/company/price fields placed, so the PDF came out mostly blank. Add them in Form Settings, or use “Reset to default template”.'
      );
    }
  }
}
